import { useState, useEffect, useRef } from 'react';
import cn from './style.module.scss';

// ==============================
// TYPES
// ==============================

export interface Tab {
  title: string;
  content: React.ReactNode;
  icon?: string | React.ReactNode;
  tabClass?: string;
  activeTabClass?: string;
}

export type SlideDirection = 'horizontal' | 'vertical';
export type MoveDirection = 'left' | 'right' | 'fade';
export type LayoutDirection = 'vertical' | 'horizontal';
export type TabChangeReason = 'click' | 'hover';

export interface DynamicTabsProps {
  tabs: Tab[];
  wrapperClassName?: string;
  className?: string;
  tabClass?: string;
  elementClass?: string;
  listClassName?: string;
  itemClassName?: string;
  tabIconClass?: string;
  linkClass?: string;
  contentClass?: string;
  contentBlockClass?: string;
  activeClassName?: string;
  bgFlowClass?: string;
  showFlowIndicator?: boolean;
  showIndenter?: boolean;
  changeOnHover?: boolean;
  disableClick?: boolean;
  triggerOnActiveClick?: boolean;
  slideDirection?: SlideDirection;
  moveDirection?: MoveDirection;
  layout?: LayoutDirection;
  defaultActiveIndex?: number;
  onTabChange?: (index: number, reason?: TabChangeReason) => void;
}

// ==============================
// UTILS
// ==============================

export const getModuleClass = (name?: string) =>
  name ? cn[name as keyof typeof cn] : '';

export const getAnimationClass = (direction: MoveDirection): string => {
  if (direction === 'right') return cn.slideInRight;
  if (direction === 'left') return cn.slideInLeft;
  return cn.fadeUp;
};

export const determineMoveDirection = (
  moveDirection: MoveDirection | undefined,
  slideDirection: 'horizontal' | 'vertical',
  currentIndex: number,
  newIndex: number
): MoveDirection => {
  if (moveDirection === 'fade') return 'fade';
  if (moveDirection === 'left') return 'left';
  if (moveDirection === 'right') return 'right';
  if (slideDirection === 'horizontal') {
    return newIndex > currentIndex ? 'right' : 'left';
  }
  return 'fade';
};

// ==============================
// HOOKS
// ==============================

export const useDynamicTabs = (
  defaultActiveIndex: number,
  showFlowIndicator: boolean,
  onTabChange?: (index: number, reason?: TabChangeReason) => void,
  moveDirection?: MoveDirection,
  slideDirection: SlideDirection = 'horizontal'
) => {
  const [activeTab, setActiveTab] = useState(defaultActiveIndex ?? 0);
  const [direction, setDirection] = useState<MoveDirection>('right');
  const [flowStyle, setFlowStyle] = useState({ left: 0, width: 0 });

  const tabRefs = useRef<HTMLLIElement[]>([]);

  useEffect(() => {
    if (showFlowIndicator && tabRefs.current[activeTab]) {
      const { offsetLeft, offsetWidth } = tabRefs.current[activeTab];
      setFlowStyle({ left: offsetLeft, width: offsetWidth });
    }
  }, [activeTab, showFlowIndicator]);

  useEffect(() => {
    setActiveTab(defaultActiveIndex ?? 0);
  }, [defaultActiveIndex]);

  const changeTab = (
    index: number,
    reason: TabChangeReason = 'click',
    triggerOnActiveClick?: boolean
  ) => {
    if (index === activeTab) {
      if (reason === 'click' && triggerOnActiveClick) {
        onTabChange?.(index, reason);
      }
      return;
    }

    setDirection(
      determineMoveDirection(moveDirection, slideDirection, activeTab, index)
    );
    setActiveTab(index);
    onTabChange?.(index, reason);
  };

  return {
    activeTab,
    direction,
    flowStyle,
    tabRefs,
    changeTab,
  };
};
